/**
 * `kind: COMPONENT` with `component: "sfdc-account"`.
 *
 * ## Two forms, decided by the service
 *
 * The live AgentHub version swaps the question's own TYPE at runtime:
 *
 *     questionType: sfdcAccounts.length > 0 ? 'LIST_SELECTION' : 'TEXT_INPUT'
 *     answerOptions: accounts.map(a => ({ optionText: `${name} (${id})`, value: a.accountId }))
 *
 * So the dropdown is the main path — a plain single-select whose options came from a service —
 * and free text is the fallback for a caller who owns no accounts. An earlier version of this
 * file implemented only the text box, which had the main path backwards.
 *
 * This keeps the current UI rather than copying AgentHub's: the same field styling as the rest
 * of the questionnaire, with the account list as a native select, and verification shown
 * inline instead of in a modal.
 *
 * ## Verification happens on Next, not on a button
 *
 * `GetSfdcAccountInfo({ sfdcAccountId })` IS the verification — found means valid. The live
 * code runs it inside its Next handler and refuses to advance when it fails:
 *
 *     const validatedAccount = await ValidateSfdcAccountId(inputSfdcAccountId);
 *     if (!validatedAccount) { setSfdcAccountError(...); setIsNextLoading(false); return; }
 *
 * This file first offered a separate 校验 button instead, which was a real defect rather than
 * a styling choice: `nextDisabled` only asked whether the field was non-empty, so a BD could
 * type eighteen arbitrary characters, skip the button, and carry a non-existent account id
 * through `CreateQuestionnaire` and `SubmitQuestionnaire`. It now registers a gate (see
 * `AnswerControlProps.registerGate`) so Next does the check and blocks on failure.
 *
 * The dropdown branch registers no gate — the id came from the service, so looking it up again
 * would only confirm what it just told us, at the cost of a round trip per question.
 *
 * Three outcomes, not two. The AgentHub wrapper collapses a lookup failure and a service error
 * into one `null`, so a BD hitting an outage is told their correct id is wrong — and checks it
 * again. Here they read differently, because that difference is the entire message.
 *
 * Still not done, and visible as such: the prompt says 18 characters and nothing checks the
 * length. Left alone because adding the check changes who can submit, which is a product call.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { AnswerControlProps } from '../answerTypes';
import { type SfdcAccount, type VerifyResult, getSfdcAccountApi } from './sfdcAccounts';
import './SfdcAccountAnswer.css';

/** Replaced by the caller's alias once this is wired to the id token. */
const MOCK_OWNER_ALIAS = 'liukp';

export default function SfdcAccountAnswer({
  value,
  onChange,
  readOnly,
  registerGate,
}: AnswerControlProps) {
  const text = typeof value === 'string' ? value : '';

  const [accounts, setAccounts] = useState<SfdcAccount[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState<VerifyResult | null>(null);

  /*
   * The gate runs long after it is registered, so it reads the CURRENT value and account list
   * through refs. Closing over `text` instead would mean re-registering on every keystroke,
   * and the window between a keystroke and the re-register is one where Next would validate
   * the previous value.
   */
  const textRef = useRef(text);
  textRef.current = text;
  const accountsRef = useRef<SfdcAccount[] | null>(accounts);
  accountsRef.current = accounts;

  const gate = useCallback(async () => {
    // Dropdown branch: the value is a service-supplied id, nothing to check.
    if (accountsRef.current && accountsRef.current.length > 0) return true;

    const id = textRef.current.trim();
    if (!id) return false;

    setVerifying(true);
    try {
      const r = await getSfdcAccountApi().verify(id);
      setVerified(r);
      // `error` blocks too: an unreachable service means we do not KNOW the id is good, and
      // letting it through would put an unverified id into the submission — the same outcome
      // the separate-button version produced.
      return r.kind === 'valid';
    } finally {
      setVerifying(false);
    }
  }, []);

  useEffect(() => {
    if (!registerGate) return;
    registerGate(gate);
    return () => registerGate(null);
  }, [registerGate, gate]);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const list = await getSfdcAccountApi().listOwnAccounts(MOCK_OWNER_ALIAS);
        if (live) setAccounts(list);
      } catch (e) {
        // Which service failed matters: this one is not the CPA backend, so "the
        // questionnaire is broken" would point at the wrong thing.
        if (live) {
          setAccounts([]);
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  // A typed id stops being verified the moment it changes, or the tick would vouch for a
  // different id than the one in the field.
  useEffect(() => {
    setVerified(null);
  }, [text]);

  if (accounts === null) {
    return <div className="cpa-sfdc__loading">正在读取你名下的 SFDC 账户…</div>;
  }

  const hasAccounts = accounts.length > 0;

  if (hasAccounts) {
    return (
      <div className="cpa-sfdc">
        {/*
          A native select, not Cloudscape's: the options are "company name (id)" strings from
          another service, and this control already owns its markup for the sake of the
          magnifier on the other branch. One field style for both branches beats two.
        */}
        <select
          className="cpa-sfdc__select"
          value={text}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          <option value="">选择一个账户</option>
          {accounts.map((a) => (
            <option key={a.accountId} value={a.accountId}>
              {a.companyNameZh || a.companyNameEn} ({a.accountId})
            </option>
          ))}
        </select>
        {/* No verification on this branch: the id came from the service, so looking it up
            again would only confirm what it just told us. */}
      </div>
    );
  }

  return (
    <div className="cpa-sfdc">
      <div className="cpa-sfdc__row">
        <div className="cpa-sfdc__input-wrap">
          <span className="cpa-sfdc__icon" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="15" height="15">
              <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <path
                d="M10.4 10.4L14 14"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <input
            className="cpa-sfdc__input"
            type="text"
            readOnly={readOnly || verifying}
            value={text}
            placeholder="Enter your account"
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        </div>
      </div>

      {/* No 校验 button: Next does it. See the gate above. */}
      {verifying && <div className="cpa-sfdc__msg">正在校验这个账户 ID…</div>}

      {loadError && (
        <div className="cpa-sfdc__msg cpa-sfdc__msg--warn">
          没能读取你名下的账户（{loadError}）—— 可以手动输入账户 ID。
        </div>
      )}

      {verified?.kind === 'valid' && (
        <div className="cpa-sfdc__msg cpa-sfdc__msg--ok">
          ✓ {verified.account.companyNameZh || verified.account.companyNameEn}
        </div>
      )}
      {verified?.kind === 'invalid' && (
        <div className="cpa-sfdc__msg cpa-sfdc__msg--bad">没有找到这个账户 ID。</div>
      )}
      {verified?.kind === 'error' && (
        // Distinct wording from "not found", so nobody re-checks a correct id during an outage.
        <div className="cpa-sfdc__msg cpa-sfdc__msg--warn">
          查询服务暂时不可用（{verified.message}），这不代表账户 ID 有问题。
        </div>
      )}
    </div>
  );
}
